/**
 * EditUserModal Component
 *
 * Modal for editing user details (admin only).
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { useUpdateUser } from '../hooks/useUpdateUser';
import { useAuth } from '../../../contexts/AuthContext';
import type { UserSanitized } from '../../../types/user.types';
import { getErrorMessage } from '../../../lib/utils/errors';
import { nameSchema } from '../../../lib/validators/common';

const updateUserSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  status: z.enum(['active', 'inactive', 'suspended']),
});

type UpdateUserFormData = z.infer<typeof updateUserSchema>;

interface EditUserModalProps {
  user: UserSanitized | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { tenant, user: currentUser } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const updateUserMutation = useUpdateUser(tenantId);
  const isCurrentUser = user?.userId === currentUser?.userId;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<UpdateUserFormData>({
    resolver: zodResolver(updateUserSchema),
  });

  useEffect(() => {
    if (user && isOpen) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        status: user.status as 'active' | 'inactive' | 'suspended',
      });
    }
  }, [user, isOpen, reset]);

  const onSubmit = async (data: UpdateUserFormData) => {
    if (!user) return;

    try {
      await updateUserMutation.mutateAsync({
        userId: user.userId,
        data,
      });
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleClose = () => {
    reset();
    updateUserMutation.reset();
    onClose();
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Edit User</Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="space-y-4">
            {updateUserMutation.isError && (
              <Alert variant="error">
                {getErrorMessage(updateUserMutation.error)}
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <Input
                  {...register('firstName')}
                  error={errors.firstName?.message}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <Input
                  {...register('lastName')}
                  error={errors.lastName?.message}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <p className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded-md">
                {user.email}
              </p>
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <Select {...register('status')} disabled={isSubmitting || isCurrentUser}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </Select>
              {isCurrentUser && (
                <p className="text-xs text-amber-600 mt-1">
                  You cannot change your own status
                </p>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
